import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Select,
  Space,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useScreen } from 'hooks/useScreen';
import { useState } from 'react';

import { queryErrorMessage } from '../CashTransactions/types';
import type { CompetitionOption, Participant, RoomOption } from './types';

interface FormValues {
  submission_key?: string;
  submitted_at?: Dayjs | null;
  last_name?: string;
  first_name: string;
  competition_class_year_id: number;
  school_name?: string;
  participant_class_year?: string;
  mobile_no: string;
  email_address?: string;
  participation_no?: number | null;
  payment_proof_url?: string;
  exam_room_id?: number | null;
  exam_number?: string;
}

// Adds a Participant by hand, or corrects or deletes one.
export default function ParticipantDrawer({
  competition,
  rooms,
  participant,
  canEdit,
  onClose,
  onSaved,
}: {
  competition: CompetitionOption;
  rooms: RoomOption[];
  participant: Participant | null;
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { runQuery } = useScreen();
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isOlympiad = competition.type === 'OLYMPIAD';

  const initialValues: Partial<FormValues> = participant
    ? {
        ...participant,
        mobile_no: participant.mobile_no.join(', '),
        last_name: participant.last_name ?? undefined,
        school_name: participant.school_name ?? undefined,
        participant_class_year: participant.participant_class_year ?? undefined,
        email_address: participant.email_address ?? undefined,
        payment_proof_url: participant.payment_proof_url ?? undefined,
        exam_number: participant.exam_number ?? undefined,
        submitted_at: participant.submitted_at ? dayjs(participant.submitted_at) : null,
      }
    : {};

  const save = async (values: FormValues) => {
    setSaving(true);
    try {
      await runQuery('competition_participant_save', {
        id: participant?.id,
        competitionId: competition.id,
        values: {
          ...values,
          submitted_at: values.submitted_at ? values.submitted_at.toISOString() : null,
          participation_no: values.participation_no ?? null,
          exam_room_id: values.exam_room_id ?? null,
        },
      });
      message.success('Хадгаллаа.');
      onSaved();
    } catch (e) {
      message.error(queryErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!participant) return;
    setDeleting(true);
    try {
      await runQuery('competition_participant_delete', { participantId: participant.id });
      message.success('Устгалаа.');
      onSaved();
    } catch (e) {
      message.error(queryErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Drawer
      title={participant ? 'Оролцогч засах' : 'Оролцогч нэмэх'}
      open
      onClose={onClose}
      width={480}
      extra={
        canEdit && (
          <Space>
            {participant && (
              <Popconfirm
                title="Оролцогчийг устгах уу?"
                okText="Устгах"
                okButtonProps={{ danger: true }}
                cancelText="Болих"
                onConfirm={remove}
              >
                <Button danger loading={deleting}>
                  Устгах
                </Button>
              </Popconfirm>
            )}
            <Button type="primary" loading={saving} onClick={() => form.submit()}>
              Хадгалах
            </Button>
          </Space>
        )
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={save}
        disabled={!canEdit}
      >
        {/* Never edited: an import matches rows by it; a new one gets its own. */}
        {participant && (
          <Form.Item name="submission_key" label="Бүртгэлийн түлхүүр">
            <Input disabled />
          </Form.Item>
        )}
        <Form.Item name="submitted_at" label="Бүртгүүлсэн огноо">
          <DatePicker showTime format="YYYY.MM.DD HH:mm" style={{ width: '100%' }} />
        </Form.Item>
        <Space.Compact block>
          <Form.Item name="last_name" label="Овог" style={{ flex: 1 }}>
            <Input />
          </Form.Item>
          <Form.Item
            name="first_name"
            label="Нэр"
            rules={[{ required: true, message: 'Нэр оруулна уу.' }]}
            style={{ flex: 1 }}
          >
            <Input />
          </Form.Item>
        </Space.Compact>
        <Form.Item
          name="competition_class_year_id"
          label="Тэмцээний анги"
          rules={[{ required: true, message: 'Анги сонгоно уу.' }]}
        >
          <Select
            options={competition.class_years.map((cy) => ({ value: cy.id, label: cy.name }))}
          />
        </Form.Item>
        <Form.Item name="school_name" label="Сургууль">
          <Input />
        </Form.Item>
        <Form.Item name="participant_class_year" label="Суралцдаг анги">
          <Input />
        </Form.Item>
        <Form.Item
          name="mobile_no"
          label="Утас"
          rules={[{ required: true, message: 'Утас оруулна уу.' }]}
          extra="Хэд хэдэн дугаарыг таслал эсвэл зайгаар тусгаарлана."
        >
          <Input />
        </Form.Item>
        <Form.Item name="email_address" label="И-мэйл">
          <Input />
        </Form.Item>
        <Form.Item name="participation_no" label="Оролцож буй удаа">
          <InputNumber min={1} max={99} style={{ width: '100%' }} />
        </Form.Item>
        {isOlympiad && (
          <Space.Compact block>
            <Form.Item name="exam_room_id" label="Шалгалтын өрөө" style={{ flex: 1 }}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={rooms.map((r) => ({ value: r.id, label: r.label }))}
              />
            </Form.Item>
            <Form.Item name="exam_number" label="Шалгалтын дугаар" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
          </Space.Compact>
        )}
        <Form.Item
          name="payment_proof_url"
          label="Төлбөрийн баримт (холбоос)"
          rules={[{ type: 'url', message: 'Холбоос буруу байна.' }]}
          extra={
            participant?.payment_proof_url && (
              <a href={participant.payment_proof_url} target="_blank" rel="noopener noreferrer">
                Баримт харах
              </a>
            )
          }
        >
          <Input />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
